import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { fetchMarketplaceListingById } from "../../services/marketplace/fetchMarketplaceListings";
import { formatCurrency } from "../../services/formatters";
import "./marketplaceItem.css";
import { getGuestCommerceToken } from "./guestCommerce";

const PLACEHOLDER = "/Logos/placeholder-item.png";
const R2_PUBLIC_BASE = "https://media.hobbli.com";

const r2PublicUrl = (objectKey) => {
  const key = String(objectKey || "").replace(/^\/+/, "");
  return key ? `${R2_PUBLIC_BASE}/${key}` : null;
};

const money = (value, currency = "USD") => {
  if (value === null || value === undefined || value === "") return null;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(Number(value));
  } catch {
    return formatCurrency(Number(value));
  }
};

const imageUrl = (image) =>
  image?.imageUrl || image?.image_url || image?.url || image?.publicUrl || null;

const imagePosition = (image) =>
  Number(image?.position ?? image?.sortOrder ?? image?.sort_order ?? 0);

const imageIsPrimary = (image) =>
  Boolean(image?.isPrimary ?? image?.is_primary);

const uniqueImages = (item) => {
  const raw = Array.isArray(item?.images) ? item.images : [];
  const normalized = raw
    .map((image, index) => ({
      id: image?.id || `image-${index}`,
      url: imageUrl(image),
      position: imagePosition(image),
      isPrimary: imageIsPrimary(image),
    }))
    .filter((image) => Boolean(image.url))
    .sort((a, b) => {
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      return a.position - b.position;
    });

  if (item?.primaryImage && !normalized.some((image) => image.url === item.primaryImage)) {
    normalized.unshift({
      id: "primary-image",
      url: item.primaryImage,
      position: -1,
      isPrimary: true,
    });
  }

  const seen = new Set();
  return normalized.filter((image) => {
    if (seen.has(image.url)) return false;
    seen.add(image.url);
    return true;
  });
};

const loadLiveInventoryGallery = async (listing) => {
  const inventoryId =
    listing?.inventoryId ||
    listing?.inventory_id ||
    listing?.sellerInventoryId ||
    listing?.seller_inventory_id;

  if (!inventoryId) return [];

  const { data, error } = await supabase
    .from("inventory_images")
    .select("id, storage_provider, storage_bucket, object_key, position, is_primary")
    .eq("seller_inventory_id", inventoryId)
    .order("position", { ascending: true });

  if (error) throw error;

  return (data || [])
    .map((image, index) => ({
      id: image.id || `inventory-image-${index}`,
      imageUrl: r2PublicUrl(image.object_key),
      position: Number(image.position ?? index),
      isPrimary: Boolean(image.is_primary),
    }))
    .filter((image) => Boolean(image.imageUrl))
    .sort((a, b) => {
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      return a.position - b.position;
    });
};

const firstValue = (...values) =>
  values.find((value) => value !== null && value !== undefined && value !== "");

const genericIdentity = (item) =>
  [
    firstValue(item?.year, item?.identity?.year),
    firstValue(item?.brand, item?.identity?.brand),
    firstValue(item?.setName, item?.set_name, item?.identity?.setName, item?.identity?.set_name),
    firstValue(item?.subset, item?.identity?.subset),
    firstValue(item?.cardNumber, item?.card_number, item?.identity?.cardNumber, item?.identity?.card_number),
  ].filter(Boolean);

export default function MarketplaceItem() {
  const { listingId } = useParams();
  const navigate = useNavigate();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selectedImage, setSelectedImage] = useState(PLACEHOLDER);
  const [session, setSession] = useState(null);

  const [quantity, setQuantity] = useState(1);
  const [cartBusy, setCartBusy] = useState(false);
  const [cartNotice, setCartNotice] = useState(null);
  const [cartState, setCartState] = useState({ quantity: 0, cartItemId: null });

  const [showOffer, setShowOffer] = useState(false);
  const [offerAmount, setOfferAmount] = useState("");
  const [offerMessage, setOfferMessage] = useState("");
  const [offerBusy, setOfferBusy] = useState(false);
  const [offerNotice, setOfferNotice] = useState(null);
  const [guestEmail, setGuestEmail] = useState("");

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (active) setSession(data?.session || null);
    });

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (active) setSession(nextSession || null);
    });

    return () => {
      active = false;
      authListener?.subscription?.unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setLoadError("");
      setCartNotice(null);
      setOfferNotice(null);

      try {
        const listing = await fetchMarketplaceListingById(listingId);
        if (!active) return;

        let resolvedListing = listing || null;

        if (listing) {
          try {
            const liveImages = await loadLiveInventoryGallery(listing);
            if (!active) return;

            if (liveImages.length > 0) {
              resolvedListing = {
                ...listing,
                images: liveImages,
                primaryImage:
                  liveImages.find((image) => image.isPrimary)?.imageUrl ||
                  liveImages[0]?.imageUrl ||
                  listing?.primaryImage ||
                  null,
              };
            }
          } catch (imageError) {
            // Keep the marketplace RPC payload as a safe fallback if the live
            // inventory gallery cannot be read for any reason.
            console.error("Live inventory gallery error:", imageError);
          }
        }

        setItem(resolvedListing);
        const gallery = uniqueImages(resolvedListing);
        setSelectedImage(
          gallery[0]?.url || resolvedListing?.primaryImage || PLACEHOLDER
        );
        setQuantity(1);
      } catch (error) {
        console.error("Marketplace listing detail error:", error);
        if (active) {
          setItem(null);
          setLoadError(error?.message || "Unable to load this listing.");
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [listingId]);

  const gallery = useMemo(() => uniqueImages(item), [item]);
  const identity = useMemo(() => genericIdentity(item), [item]);

  const available = Math.max(
    0,
    Number(firstValue(item?.quantityAvailable, item?.quantity_available, 0))
  );
  const currency = item?.currency || "USD";
  const price = firstValue(item?.priceAmount, item?.price_amount);
  const acceptsOffers = Boolean(
    firstValue(item?.acceptsOffers, item?.accepts_offers, false)
  );
  const minimumOffer = firstValue(
    item?.minimumOfferAmount,
    item?.minimum_offer_amount
  );

  const shopSlug = firstValue(item?.shop?.slug, item?.shopSlug, item?.shop_slug);
  const shopName = firstValue(item?.shop?.name, item?.shopName, item?.shop_name, "Seller shop");
  const sellerName = firstValue(
    item?.seller?.displayName,
    item?.sellerDisplayName,
    item?.seller_display_name,
    shopName
  );
  const isPlatformShop = Boolean(
    firstValue(item?.shop?.isPlatformShop, item?.isPlatformShop, item?.is_platform_shop, false)
  );

  const condition = firstValue(item?.condition, item?.conditionOverride);
  const description = firstValue(item?.description, item?.description_override);
  const attributes = item?.attributes && typeof item.attributes === "object" ? item.attributes : {};
  const grade = firstValue(
    attributes?.grade,
    attributes?.grading_grade,
    attributes?.identity?.grade
  );
  const grader = firstValue(
    attributes?.grading_company,
    attributes?.grader,
    attributes?.identity?.grading_company
  );

  const shippingMode = firstValue(item?.shippingMode, item?.shipping_mode);
  const shippingAmount = firstValue(item?.shippingAmount, item?.shipping_amount);
  const localPickup = Boolean(
    firstValue(item?.localPickupAvailable, item?.local_pickup_available, false)
  );
  const combinedShipping = Boolean(
    firstValue(item?.combinedShippingAllowed, item?.combined_shipping_allowed, false)
  );

  const shippingLabel = (() => {
    if (shippingMode === "free") return "Free shipping";
    if (shippingMode === "pickup") return "Local pickup";
    if (shippingAmount !== null && shippingAmount !== undefined && shippingAmount !== "") {
      return `${money(shippingAmount, item?.shippingCurrency || item?.shipping_currency || currency)} shipping`;
    }
    if (shippingMode === "profile") return "Shipping available";
    return "Shipping calculated from listing terms";
  })();

  const requireSignIn = (purpose) => {
    const message =
      purpose === "offer"
        ? "Sign in to make an offer. Your offer history will stay tied to your Hobbli account."
        : "Sign in to add this collectible to your Hobbli cart.";
    if (purpose === "offer") {
      setOfferNotice({ type: "info", message });
    } else {
      setCartNotice({ type: "info", message });
    }
    return false;
  };

  const refreshListingCartState = async () => {
    if (!item?.listingId && !item?.listing_id) return;
    try {
      const rpc = session?.user?.id ? "get_my_cart_summary" : "get_guest_cart_summary";
      const args = session?.user?.id ? {} : { p_guest_token: getGuestCommerceToken() };
      const { data, error } = await supabase.rpc(rpc, args);
      if (error) throw error;
      const rows = Array.isArray(data?.items) ? data.items : [];
      const match = rows.find((row) => row.listing_id === (item.listingId || item.listing_id));
      setCartState({
        quantity: Number(match?.quantity || 0),
        cartItemId: match?.cart_item_id || null,
      });
    } catch (error) {
      console.error("Cart state lookup error:", error);
    }
  };

  useEffect(() => {
    refreshListingCartState();
  }, [item?.listingId, item?.listing_id, session?.user?.id]);

  const addToCart = async () => {
    setCartNotice(null);
    if (!item?.listingId && !item?.listing_id) return;

    if (cartState.quantity > 0 && available <= cartState.quantity) {
      navigate("/cart");
      return;
    }

    setCartBusy(true);
    try {
      const rpc = session?.user?.id ? "add_listing_to_cart" : "add_listing_to_guest_cart";
      const args = session?.user?.id
        ? { p_listing_id: item.listingId || item.listing_id, p_quantity: quantity }
        : {
            p_guest_token: getGuestCommerceToken(),
            p_listing_id: item.listingId || item.listing_id,
            p_quantity: quantity,
          };

      const { error } = await supabase.rpc(rpc, args);
      if (error) throw error;

      await refreshListingCartState();
      setCartNotice({
        type: "success",
        message: "Added to your cart.",
      });
    } catch (error) {
      console.error("Add to cart error:", error);
      setCartNotice({
        type: "error",
        message: error?.message || "Unable to add this listing to your cart.",
      });
    } finally {
      setCartBusy(false);
    }
  };

  const openOffer = () => {
    setOfferNotice(null);
    setShowOffer(true);
  };

  const submitOffer = async (event) => {
    event.preventDefault();
    setOfferNotice(null);

    const amount = Number(offerAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setOfferNotice({ type: "error", message: "Enter a valid offer amount." });
      return;
    }
    if (minimumOffer !== null && minimumOffer !== undefined && amount < Number(minimumOffer)) {
      setOfferNotice({ type: "error", message: `This seller's minimum offer is ${money(minimumOffer, currency)}.` });
      return;
    }
    if (!session?.user?.id && !guestEmail.trim()) {
      setOfferNotice({ type: "error", message: "Enter your email so the seller can respond to your offer." });
      return;
    }

    setOfferBusy(true);
    try {
      const rpc = session?.user?.id ? "submit_marketplace_offer" : "submit_guest_marketplace_offer";
      const args = session?.user?.id
        ? {
            p_listing_id: item.listingId || item.listing_id,
            p_amount: amount,
            p_message: offerMessage.trim() || null,
          }
        : {
            p_guest_token: getGuestCommerceToken(),
            p_email: guestEmail.trim(),
            p_listing_id: item.listingId || item.listing_id,
            p_amount: amount,
            p_message: offerMessage.trim() || null,
          };

      const { error } = await supabase.rpc(rpc, args);
      if (error) throw error;

      setOfferAmount("");
      setOfferMessage("");
      setOfferNotice({
        type: "success",
        message: "Offer sent! We'll let you know when the seller responds.",
      });
    } catch (error) {
      console.error("Submit offer error:", error);
      setOfferNotice({ type: "error", message: error?.message || "Unable to submit your offer." });
    } finally {
      setOfferBusy(false);
    }
  };

  if (loading) {
    return (
      <main className="listing-detail-page">
        <div className="listing-detail-shell">
          <div className="listing-detail-state">Loading listing…</div>
        </div>
      </main>
    );
  }

  if (!item) {
    return (
      <main className="listing-detail-page">
        <div className="listing-detail-shell">
          <button className="listing-back" type="button" onClick={() => navigate("/shop")}>
            ← Marketplace
          </button>
          <div className="listing-detail-state">
            <h1>Listing unavailable</h1>
            <p>{loadError || "This listing is no longer available."}</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="listing-detail-page">
      <div className="listing-detail-shell">
        <div className="listing-detail-topbar">
          <button className="listing-back" type="button" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <button className="listing-marketplace-link" type="button" onClick={() => navigate("/shop")}>
            Hobbli Marketplace
          </button>
        </div>

        <section className="listing-detail-grid">
          <div className="listing-gallery">
            <div className="listing-main-image-shell">
              <img
                className="listing-main-image"
                src={selectedImage || PLACEHOLDER}
                alt={item.title || "Marketplace listing"}
                onError={(event) => {
                  event.currentTarget.src = PLACEHOLDER;
                }}
              />
              {isPlatformShop && <span className="listing-official-badge">Hobbli Official</span>}
            </div>

            {gallery.length > 1 && (
              <div className="listing-thumbnails" aria-label="Listing images">
                {gallery.map((image) => (
                  <button
                    key={image.id || image.url}
                    type="button"
                    className={`listing-thumbnail ${selectedImage === image.url ? "active" : ""}`}
                    onClick={() => setSelectedImage(image.url)}
                    aria-label="View listing image"
                  >
                    <img src={image.url} alt="" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="listing-purchase-panel">
            {identity.length > 0 && (
              <div className="listing-eyebrow">{identity.join(" · ")}</div>
            )}

            <h1>{item.title || "Marketplace listing"}</h1>

            <div className="listing-price">{money(price, currency) || "Price unavailable"}</div>

            <div className="listing-facts">
              {condition && (
                <div>
                  <span>Condition</span>
                  <strong>{condition}</strong>
                </div>
              )}
              {(grader || grade) && (
                <div>
                  <span>Grade</span>
                  <strong>{[grader, grade].filter(Boolean).join(" ")}</strong>
                </div>
              )}
              <div>
                <span>Availability</span>
                <strong>{available > 1 ? `${available} available` : available === 1 ? "1 available" : "Unavailable"}</strong>
              </div>
              <div>
                <span>Shipping</span>
                <strong>{shippingLabel}</strong>
              </div>
            </div>

            {localPickup && shippingMode !== "pickup" && (
              <div className="listing-subfact">Local pickup is also available.</div>
            )}
            {combinedShipping && (
              <div className="listing-subfact">Seller allows combined shipping.</div>
            )}

            <div className="listing-buy-box">
              {available > 1 && (
                <label className="listing-quantity-control">
                  Quantity
                  <select
                    value={quantity}
                    onChange={(event) => setQuantity(Number(event.target.value))}
                  >
                    {Array.from({ length: Math.min(available, 20) }, (_, index) => index + 1).map((value) => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                  </select>
                </label>
              )}

              <button
                type="button"
                className="listing-action listing-action-primary"
                disabled={cartBusy || available < 1}
                onClick={addToCart}
              >
                {cartBusy
                  ? "Adding…"
                  : available < 1
                    ? "Unavailable"
                    : cartState.quantity > 0 && available <= cartState.quantity
                      ? "View Cart"
                      : cartState.quantity > 0
                        ? "Add Another"
                        : "Add to Cart"}
              </button>

              {acceptsOffers && (
                <button
                  type="button"
                  className="listing-action listing-action-secondary"
                  onClick={openOffer}
                >
                  Make Offer
                </button>
              )}

              {minimumOffer !== null && minimumOffer !== undefined && acceptsOffers && (
                <div className="listing-minimum-offer">
                  Seller considers offers of {money(minimumOffer, currency)} or more.
                </div>
              )}

              {cartState.quantity > 0 && (
                <button type="button" className="listing-in-cart" onClick={() => navigate("/cart")}>
                  ✓ {cartState.quantity} in your cart · View Cart
                </button>
              )}
              {cartNotice && (
                <div className={`listing-notice ${cartNotice.type}`}>{cartNotice.message}</div>
              )}
            </div>

            <button
              type="button"
              className="listing-shop-card"
              onClick={() => shopSlug && navigate(`/${shopSlug}`)}
              disabled={!shopSlug}
            >
              <div className="listing-shop-logo">
                {item?.shop?.logoUrl || item?.shopLogoUrl || item?.shop_logo_url ? (
                  <img
                    src={item?.shop?.logoUrl || item?.shopLogoUrl || item?.shop_logo_url}
                    alt=""
                  />
                ) : (
                  <span>{shopName.slice(0, 1).toUpperCase()}</span>
                )}
              </div>
              <div>
                <span>{isPlatformShop ? "Sold by" : "Shop"}</span>
                <strong>{shopName}</strong>
                {!isPlatformShop && sellerName && sellerName !== shopName && (
                  <small>Seller: {sellerName}</small>
                )}
              </div>
              {shopSlug && <b>View shop →</b>}
            </button>
          </div>
        </section>

        {showOffer && acceptsOffers && (
          <section className="listing-offer-card">
            <div className="listing-section-heading">
              <div>
                <span>Structured offer</span>
                <h2>Make an offer</h2>
              </div>
              <button type="button" onClick={() => setShowOffer(false)}>Close</button>
            </div>

            <form onSubmit={submitOffer}>
              {!session?.user?.id && (
                <label>
                  Email
                  <input
                    className="listing-guest-email"
                    type="email"
                    required
                    value={guestEmail}
                    onChange={(event) => setGuestEmail(event.target.value)}
                    placeholder="you@example.com"
                  />
                  <small>No Hobbli account required. We'll use this for offer updates.</small>
                </label>
              )}
              <label>
                Offer amount
                <div className="listing-money-input">
                  <span>$</span>
                  <input
                    type="number"
                    min={minimumOffer ?? "0.01"}
                    step="0.01"
                    value={offerAmount}
                    onChange={(event) => setOfferAmount(event.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </label>

              <label>
                Message to seller <small>Optional</small>
                <textarea
                  value={offerMessage}
                  onChange={(event) => setOfferMessage(event.target.value)}
                  placeholder="Add context for your offer…"
                />
              </label>

              {offerNotice && (
                <div className={`listing-notice ${offerNotice.type}`}>{offerNotice.message}</div>
              )}

              <div className="listing-offer-actions">
                <button type="button" className="listing-action listing-action-secondary" onClick={() => setShowOffer(false)}>
                  Cancel
                </button>
                <button type="submit" className="listing-action listing-action-primary" disabled={offerBusy}>
                  {offerBusy ? "Sending…" : "Send Offer"}
                </button>
              </div>
            </form>
          </section>
        )}

        <section className="listing-lower-grid">
          <article className="listing-info-card">
            <div className="listing-section-heading">
              <div>
                <span>About this collectible</span>
                <h2>Description</h2>
              </div>
            </div>
            {description ? (
              <p className="listing-description">{description}</p>
            ) : (
              <p className="listing-muted">The seller has not added a description.</p>
            )}
          </article>

          <aside className="listing-info-card">
            <div className="listing-section-heading">
              <div>
                <span>Purchase details</span>
                <h2>Listing information</h2>
              </div>
            </div>
            <dl className="listing-detail-list">
              {condition && <><dt>Condition</dt><dd>{condition}</dd></>}
              {(grader || grade) && <><dt>Grade</dt><dd>{[grader, grade].filter(Boolean).join(" ")}</dd></>}
              <dt>Quantity available</dt><dd>{available}</dd>
              <dt>Shipping</dt><dd>{shippingLabel}</dd>
              {localPickup && <><dt>Local pickup</dt><dd>Available</dd></>}
              {acceptsOffers && <><dt>Offers</dt><dd>Accepted</dd></>}
            </dl>
          </aside>
        </section>
      </div>
    </main>
  );
}
